import { Button, type ButtonProps } from '@progress/kendo-react-buttons'
import React from 'react'
import { type Control, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { OperatorContainer } from '~/components/elements/OperatorContainer'
import { VStack } from '~/components/elements/VStack'

import {
  FilterGroupInput,
  type FilterGroupInputProps,
} from './components/FilterGroupInput'
import {
  FilterGroupsDisplay,
  type FilterGroupsDisplayProps,
} from './components/FilterGroupsDisplay'
import styles from './FilterGroupsInput.css'
import { type FilterItem, type InputValues } from './utils'

export type FilterGroupsInputProps<
  TFilterItem extends FilterItem<TCategory>,
  TCategory extends string,
> = {
  addFilterButtonItems: FilterGroupInputProps<
    TFilterItem,
    TCategory
  >['addButtonItems']
  categoryToFilter: FilterGroupInputProps<
    TFilterItem,
    TCategory
  >['categoryToFilter']
  control: Control<InputValues<TFilterItem, TCategory>>
  'data-testid'?: string
  filterToText: FilterGroupsDisplayProps<TFilterItem, TCategory>['filterToText']
  renderInput: FilterGroupInputProps<TFilterItem, TCategory>['renderInput']
}

export const FilterGroupsInput = <
  TFilterItem extends FilterItem<TCategory>,
  TCategory extends string,
>({
  addFilterButtonItems,
  categoryToFilter,
  control,
  'data-testid': dataTestId = 'components-features-filter-groups-input',
  filterToText,
  renderInput,
}: FilterGroupsInputProps<TFilterItem, TCategory>) => {
  const { t } = useTranslation()
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'filterGroups',
  })

  const handleAddButtonClick = React.useCallback<
    EH<ButtonProps, 'onClick'>
  >(() => {
    append([[]])
  }, [append])

  return (
    <VStack
      className={styles.root}
      data-testid={dataTestId}
      padding={2}
      spacing={4}
    >
      {0 < fields.length && (
        <VStack spacing={2}>
          {fields.map((field, filterGroupIndex) => (
            <OperatorContainer
              key={field.fieldId}
              operator={filterGroupIndex !== 0 ? 'OR' : null}
              size={80}
            >
              <FilterGroupInput
                addButtonItems={addFilterButtonItems}
                categoryToFilter={categoryToFilter}
                control={control}
                filterGroupIndex={filterGroupIndex}
                renderInput={renderInput}
                onDelete={remove}
              />
            </OperatorContainer>
          ))}
        </VStack>
      )}

      <Button
        className={styles.addButton}
        icon="plus-outline"
        onClick={handleAddButtonClick}
      >
        {t('add_or_definition')}
      </Button>

      <FilterGroupsDisplay control={control} filterToText={filterToText} />
    </VStack>
  )
}
